import { useCallback, useMemo } from 'react';
import { distinctUntilChanged, map } from 'rxjs';
import { isEqual } from 'lodash';
import { dispatchAction, useStream } from '@ardoq/rxbeach';
import type { PersonalSettingMap } from '@ardoq/api-types';
import { type UpdatePersonalSettingUnion, setPersonalSetting } from './actions';
import { loadedCurrentUser$ } from './currentUser$';

type StoredPersonalSetting<K extends keyof PersonalSettingMap> = {
  value: PersonalSettingMap[K] | undefined;
};

const NOT_LOADED = { value: undefined };

/**
 * Reads a personal setting of the current user and returns a setter that
 * replaces the stored value and persists it. The value is `undefined` until the user has loaded
 * and while the setting has never been stored.
 */
export const usePersonalSetting = <K extends keyof PersonalSettingMap>(
  personalSetting: K
): [
  PersonalSettingMap[K] | undefined,
  (newValue: PersonalSettingMap[K]) => void,
] => {
  const setting$ = useMemo(
    () =>
      loadedCurrentUser$.pipe(
        map(user => user.settings[personalSetting]),
        distinctUntilChanged(isEqual),
        map((value): StoredPersonalSetting<K> => ({ value }))
      ),
    [personalSetting]
  );
  const { value } = useStream(setting$, NOT_LOADED);
  const setValue = useCallback(
    (newValue: PersonalSettingMap[K]) =>
      dispatchAction(
        setPersonalSetting({
          personalSetting,
          newValue,
        } as UpdatePersonalSettingUnion)
      ),
    [personalSetting]
  );
  return [value, setValue];
};
